import { useQuery } from '@tanstack/react-query';
import { http } from 'services/http';
import { WAREHOUSE_TASKS_GET_MANY, WAREHOUSE_TASKS_SEARCH_HINTS } from 'api/query-keys.ts';
import type { TWarehouseTaskRes } from 'api/swagger';
import { PAGINATION_DEFAULT_LIMIT } from 'shared/constants/paginations';
import type { IWhTaskFilter } from 'shared/stores/use-wh-task-filters';
import { setFiltersToQs } from 'shared/utils/filters/set-filters-to-qs';
import type { WithPagination } from 'shared/utils/pagination.ts';
import { preparePagination } from 'shared/utils/pagination.ts';
import type { IWithPagination } from 'types/api-query.ts';

interface IParams extends IWithPagination {
  filters?: IWhTaskFilter;
  search?: string;
  isSkip?: boolean;
  isHints?: boolean;
}

export const useGetManyWarehouseTasks = (params: IParams) => {
  const { limit = PAGINATION_DEFAULT_LIMIT, page = 1, search, filters, isSkip = false, isHints = false } = params;

  const qs = new URLSearchParams({ limit: String(limit), page: String(page) });
  if (search) qs.set('filter[search]', search);
  if (filters) setFiltersToQs<IWhTaskFilter>(qs, filters);

  return useQuery<WithPagination<TWarehouseTaskRes[]>>({
    queryKey: isHints ? [WAREHOUSE_TASKS_SEARCH_HINTS, search] : [WAREHOUSE_TASKS_GET_MANY, qs.toString()],
    queryFn: async () => {
      const { data, headers } = await http.get<TWarehouseTaskRes[]>(`/v1/warehouse-tasks?${qs.toString()}`);
      const pagination = preparePagination(headers);

      return { data, pagination };
    },
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 0,
    cacheTime: 0,
    enabled: !isSkip,
  });
};
